import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../../dialog';
import { Button } from '../../../button/button';
import { Input } from '../../../input';
import LoadingSpinner from '../../../loading-spinner';
import { useState } from 'react';
import clsx from 'clsx';
import { Trash } from 'lucide-react';
import { deleteWorkspace } from '../../../../../lib/api';
import { supabase } from '../../../../../lib/supabase';
import { DeleteWorkspaceProps } from './types';

const CONFIRMATION_TEXT = 'CONFIRM';

export function DeleteWorkspace({
  authUser,
  fetchWorkspaceProfiles,
}: DeleteWorkspaceProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [confirmInput, setConfirmInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleDelete = async () => {
    setError(null);
    setSuccess(null);
    setIsLoading(true);

    try {
      await deleteWorkspace({
        workspaceId: authUser?.currentWorkspace?.workspace_id || '',
      });

      supabase.auth.getSession().then(({ data: { session } }) => {
        if (session?.user) {
          fetchWorkspaceProfiles(session?.user);
        }
      });

      setSuccess('Workspace deleted successfully.');
    } catch (err) {
      console.error('Error deleting workspace:', err);
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to delete workspace. Please try again.',
      );
    } finally {
      setIsLoading(false);
      setConfirmInput('');
    }
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" className="text-red-600 hover:text-red-700">
          Delete
        </Button>
      </DialogTrigger>

      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete workspace</DialogTitle>
          <DialogDescription>
            This action cannot be undone. This will permanently delete the
            workspace and remove all associated data.
            <br />
            Please type <strong>{CONFIRMATION_TEXT}</strong> to confirm and
            proceed with the deletion.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-3 flex flex-col gap-2">
          <Input
            disabled={isLoading}
            value={confirmInput}
            onChange={(e) => setConfirmInput(e.target.value)}
            placeholder="Type CONFIRM to delete the workspace"
          />

          {error && <p className="text-sm text-red-500">{error}</p>}
          {success && <p className="text-sm text-green-600">{success}</p>}
        </div>

        <DialogFooter>
          <Button
            onClick={handleDelete}
            disabled={isLoading || CONFIRMATION_TEXT !== confirmInput}
            variant="outline"
            className={clsx(
              'text-red-600 hover:text-red-700',
              isLoading && 'px-11',
            )}
          >
            {isLoading ? (
              <LoadingSpinner strokeWidth={7} />
            ) : (
              <>
                <Trash />
                Delete
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
